import React, { useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { useEffect } from 'react';
import emailjs from '@emailjs/browser';
import PublicIcon from '@mui/icons-material/Public';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import LanguageIcon from '@mui/icons-material/Language';
import SellIcon from '@mui/icons-material/Sell';
import GroupsIcon from '@mui/icons-material/Groups';
import DomainVerificationIcon from '@mui/icons-material/DomainVerification';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import CodeIcon from '@mui/icons-material/Code';
import StorageIcon from '@mui/icons-material/Storage';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import CampaignIcon from '@mui/icons-material/Campaign';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShieldIcon from '@mui/icons-material/Shield';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaletteIcon from '@mui/icons-material/Palette';
import './index.css';

export default function App() {
  const [theme, setTheme] = useState('dark');
  const [language, setLanguage] = useState('en');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    phone: '',
    message: '',
  });
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });
  const heroRef = useRef(null);
  const badgeRef = useRef(null);

  const isDark = theme === 'dark';

  useEffect(() => {
    if (!heroRef.current || !badgeRef.current) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        heroRef.current.querySelectorAll('.hero-reveal'),
        { opacity: 0, y: 30 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.12,
          ease: 'power3.out',
        }
      );

      gsap.to(badgeRef.current, {
        y: -6,
        repeat: -1,
        yoyo: true,
        duration: 1.8,
        ease: 'sine.inOut',
      });
    }, heroRef);

    return () => ctx.revert();
  }, []);

  const t = useMemo(() => {
    const content = {
      en: {
        badge: 'Premium Domain for Sale',
        titlePrefix: 'Own',
        domain: 'raswapanews.com',
        heroText:
          'A clean, memorable domain for a modern news or media platform. Perfect for digital publishing, public communication, and a premium brand presence.',
        priceLabel: 'Selling Price',
        ctaPrimary: 'Contact Now',
        ctaSecondary: 'View Services',
        available: 'Available',
        whyLabel: 'Why this domain?',
        whyTitle: 'A premium digital identity for a serious media platform',
        trustTitle: 'Why clients trust our team',
        trustText:
          'This page is designed to show a clean visual system, modern interactions, and serious full-stack delivery capability.',
        servicesLabel: 'Development Services',
        servicesTitle: 'Need the website too?',
        servicesText:
          'Along with the domain, we can build the full product: frontend, backend, CMS, admin dashboard, deployment, and support.',
        contactLabel: 'Contact',
        contactTitle: 'Contact for domain purchase or full-stack development',
        contactText:
          'Send your inquiry for domain purchase, website design, or complete media portal development.',
        domainPrice: 'Domain Price',
        email: 'Email',
        emailValue: 'raswapanews@gmail.com',
        serviceInfo: 'Services',
        serviceInfoValue: 'Domain Sale + Full-Stack Website Development',
        formTitle: 'Send Message with EmailJS',
        formSub: 'Frontend contact form integration',
        name: 'Full Name',
        emailField: 'Email Address',
        subject: 'Subject',
        phone: 'Phone / WhatsApp',
        message: 'Message',
        send: 'Send Inquiry',
        sending: 'Sending...',
        emailBtn: 'Email Us',
        quoteBtn: 'Get Website Quote',
        footer: 'Domain available for sale and full-stack development support.',
        langSwitch: 'EN / NP',
        light: 'Light Mode',
        dark: 'Dark Mode',
        features: [
          {
            icon: DomainVerificationIcon,
            title: 'Strong Brand Identity',
            description: 'Short, serious, and memorable for a trusted media brand.',
          },
          {
            icon: VerifiedIcon,
            title: 'Launch-Ready Positioning',
            description: 'Suitable for a news portal, media organization, or campaign platform.',
          },
          {
            icon: GroupsIcon,
            title: 'Professional Team Support',
            description: 'Frontend, backend, dashboard, CMS, and deployment support available.',
          },
        ],
        trustCards: [
          { icon: PaletteIcon, title: 'Professional UI/UX Execution' },
          { icon: TrendingUpIcon, title: 'Scalable Frontend Structure' },
          { icon: ShieldIcon, title: 'Business-Focused Planning' },
          { icon: RocketLaunchIcon, title: 'Launch-Ready Delivery' },
        ],
        services: [
          { icon: CodeIcon, title: 'React Frontend Development' },
          { icon: StorageIcon, title: 'Backend & API Integration' },
          { icon: DashboardCustomizeIcon, title: 'Admin Panel / CMS Setup' },
          { icon: CampaignIcon, title: 'News Portal UI/UX Design' },
          { icon: PublicIcon, title: 'Domain & Hosting Setup' },
          { icon: RocketLaunchIcon, title: 'Deployment & Maintenance' },
        ],
        success: 'Your inquiry has been sent successfully.',
        error: 'Failed to send message. Please try again.',
      },
      np: {
        badge: 'प्रिमियम डोमेन बिक्रीमा',
        titlePrefix: 'किन्नुहोस्',
        domain: 'raswapanews.com',
        // heroText:'आधुनिक समाचार वा मिडिया प्लेटफर्मका लागि सफा र सजिलै सम्झन सकिने डोमेन। डिजिटल प्रकाशन, सार्वजनिक संचार र प्रिमियम ब्रान्ड उपस्थितिका लागि उपयुक्त।',
        priceLabel: 'बिक्री मूल्य',
        ctaPrimary: 'सम्पर्क गर्नुहोस्',
        ctaSecondary: 'सेवा हेर्नुहोस्',
        available: 'उपलब्ध',
        whyLabel: 'यो डोमेन किन?',
        // whyTitle: 'गम्भीर मिडिया प्लेटफर्मका लागि प्रिमियम डिजिटल पहिचान',
        trustTitle: 'हाम्रो टिममा client किन विश्वास गर्छन्',
        trustText:
          'यो page ले clean visual system, modern interaction, र serious full-stack delivery capability देखाउँछ।',
        servicesLabel: 'डेभलपमेन्ट सेवा',
        servicesTitle: 'वेबसाइट पनि चाहिन्छ?',
        servicesText:
          'डोमेनसँगै हामी frontend, backend, CMS, admin dashboard, deployment र support सहित पूरा product निर्माण गर्न सक्छौं।',
        contactLabel: 'सम्पर्क',
        contactTitle: 'डोमेन खरिद वा सम्पूर्ण वेबसाइट बनाउनको लागि सम्पर्क गर्नुहोस्',
        contactText:
          'डोमेन purchase, website design, वा complete media portal development का लागि inquiry पठाउनुहोस्।',
        domainPrice: 'डोमेन मूल्य',
        email: 'इमेल',
        emailValue: 'raswapanews@gmail.com',
        serviceInfo: 'सेवाहरू',
        serviceInfoValue: 'डोमेन बिक्री + Full-Stack Website Development',
        formTitle: 'Email बाट सन्देश पठाउनुहोस्',
        formSub: 'Frontend contact form integration',
        name: 'पूरा नाम',
        emailField: 'इमेल ठेगाना',
        subject: 'विषय',
        phone: 'फोन / WhatsApp',
        message: 'सन्देश',
        send: 'Inquiry पठाउनुहोस्',
        sending: 'पठाइँदै...',
        emailBtn: 'इमेल गर्नुहोस्',
        quoteBtn: 'वेबसाइट Quote लिनुहोस्',
        footer: 'डोमेन बिक्रीमा उपलब्ध छ र full-stack development support पनि उपलब्ध छ।',
        langSwitch: 'NP / EN',
        light: 'Light Mode',
        dark: 'Dark Mode',
        features: [
          {
            icon: DomainVerificationIcon,
            title: 'बलियो ब्रान्ड पहिचान',
            description: 'विश्वसनीय मिडिया ब्रान्डका लागि छोटो, गम्भीर र सम्झन योग्य।',
          },
          {
            icon: VerifiedIcon,
            title: 'लन्च-रेडी पोजिसनिङ',
            description: 'News portal, media organization, वा campaign platform का लागि उपयुक्त।',
          },
          {
            icon: GroupsIcon,
            title: 'प्रोफेशनल टिम सपोर्ट',
            description: 'Frontend, backend, dashboard, CMS र deployment support उपलब्ध।',
          },
        ],
        trustCards: [
          { icon: PaletteIcon, title: 'Professional UI/UX Execution' },
          { icon: TrendingUpIcon, title: 'Scalable Frontend Structure' },
          { icon: ShieldIcon, title: 'Business-Focused Planning' },
          { icon: RocketLaunchIcon, title: 'Launch-Ready Delivery' },
        ],
        services: [
          { icon: CodeIcon, title: 'React Frontend Development' },
          { icon: StorageIcon, title: 'Backend & API Integration' },
          { icon: DashboardCustomizeIcon, title: 'Admin Panel / CMS Setup' },
          { icon: CampaignIcon, title: 'News Portal UI/UX Design' },
          { icon: PublicIcon, title: 'Domain & Hosting Setup' },
          { icon: RocketLaunchIcon, title: 'Deployment & Maintenance' },
        ],
        success: 'तपाईंको inquiry सफलतापूर्वक पठाइयो।',
        error: 'सन्देश पठाउन सकिएन। फेरि प्रयास गर्नुहोस्।',
      },
    };
    return content[language];
  }, [language]);

  const panelClasses = isDark
    ? 'border-white/10 bg-white/5 text-slate-200'
    : 'border-slate-200 bg-white text-slate-700 shadow-sm';
  const mutedText = isDark ? 'text-slate-300' : 'text-slate-600';
  const subMutedText = isDark ? 'text-slate-400' : 'text-slate-500';
  const pageClasses = isDark ? 'bg-slate-950 text-white' : 'bg-slate-50 text-slate-900';
  const heroGradient = isDark ? 'from-slate-950 via-slate-900 to-red-950/40' : 'from-white via-red-50 to-slate-100';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSending(true);
    setStatus({ type: '', message: '' });

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        {
          name: formData.name,
          email: formData.email,
          subject: formData.subject,
          phone: formData.phone,
          message: formData.message,
          domain_name: 'raswapanews.com',
          offer_price: '$962',
        },
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      setStatus({ type: 'success', message: t.success });
      setFormData({ name: '', email: '', subject: '', phone: '', message: '' });
    } catch (error) {
      setStatus({ type: 'error', message: t.error });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className={`min-h-screen overflow-hidden transition-colors duration-500 ${pageClasses}`}>
      {/* ===== NEWS TICKER BACKGROUND ===== */}
<div className="fixed inset-0 -z-20 overflow-hidden pointer-events-none">
  <div className="absolute top-20 w-full whitespace-nowrap opacity-10">
    <motion.div
      animate={{ x: ['0%', '-100%'] }}
      transition={{ repeat: Infinity, duration: 30, ease: 'linear' }}
      className="flex gap-16 text-6xl font-bold tracking-wide text-red-500"
    >
      {[
        'Breaking News',
        'Politics',
        'Economy',
        'Technology',
        'Global Updates',
        'Media',
        'Live Reports',
        'Digital News',
        'Press',
        'Journalism'
      ].map((text, i) => (
        <span key={i}>{text}</span>
      ))}
    </motion.div>
  </div>

  <div className="absolute top-1/2 w-full whitespace-nowrap opacity-5">
    <motion.div
      animate={{ x: ['-100%', '0%'] }}
      transition={{ repeat: Infinity, duration: 40, ease: 'linear' }}
      className="flex gap-20 text-7xl font-extrabold tracking-wide text-white"
    >
      {[
        'RASWAPA NEWS',
        'LIVE',
        'TRENDING',
        'HEADLINES',
        'UPDATE',
        'MEDIA HUB',
        'GLOBAL'
      ].map((text, i) => (
        <span key={i}>{text}</span>
      ))}
    </motion.div>
  </div>

  <div className="absolute bottom-20 w-full whitespace-nowrap opacity-10">
    <motion.div
      animate={{ x: ['0%', '-100%'] }}
      transition={{ repeat: Infinity, duration: 35, ease: 'linear' }}
      className="flex gap-14 text-5xl font-semibold tracking-wide text-red-400"
    >
      {[
        'नेपाल समाचार',
        'राजनीति',
        'अर्थतन्त्र',
        'प्रविधि',
        'विश्व समाचार',
        'मिडिया',
        'ताजा अपडेट'
      ].map((text, i) => (
        <span key={i}>{text}</span>
      ))}
    </motion.div>
  </div>
</div>
      <section className="relative">


        
        <div className={`absolute inset-0 -z-10 bg-gradient-to-br ${heroGradient}`} />
        <div className="mx-auto max-w-7xl px-6 pt-6 lg:px-10">
          <div className={`flex flex-wrap items-center justify-between gap-4 rounded-3xl border p-4 backdrop-blur-xl ${panelClasses}`}>
            <div className="flex items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-red-600 text-white shadow-lg">
                <PublicIcon fontSize="small" />
              </div>
              <div>
                <p className={`text-xs font-medium uppercase tracking-[0.25em] ${subMutedText}`}>Raswapa News</p>
                <h2 className="text-lg font-bold">raswapanews.com</h2>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setLanguage(language === 'en' ? 'np' : 'en')}
                className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold transition ${panelClasses}`}
              >
                <LanguageIcon style={{ fontSize: 18 }} />
                {t.langSwitch}
              </button>
              <button
                onClick={() => setTheme(isDark ? 'light' : 'dark')}
                className={`inline-flex items-center gap-2 rounded-2xl border px-4 py-2 text-sm font-semibold transition ${panelClasses}`}
              >
                {isDark ? <LightModeIcon style={{ fontSize: 18 }} /> : <DarkModeIcon style={{ fontSize: 18 }} />}
                {isDark ? t.light : t.dark}
              </button>
            </div>
          </div>
        </div>

        <div ref={heroRef} className="mx-auto max-w-7xl px-6 pb-20 pt-12 lg:px-10 lg:pb-24 lg:pt-16">
          <div className="grid items-center gap-12 lg:grid-cols-2">
            <div>
              <div
                ref={badgeRef}
                className="hero-reveal mb-5 inline-flex items-center gap-2 rounded-full border border-red-500/30 bg-red-500/10 px-4 py-1 text-sm font-medium text-red-400"
              >
                <SellIcon style={{ fontSize: 16 }} />
                {t.badge}
              </div>

              <h1 className="hero-reveal text-4xl font-bold leading-tight sm:text-5xl lg:text-6xl">
                {t.titlePrefix} <span className="text-red-500">{t.domain}</span>
              </h1>

              <p className={`hero-reveal mt-6 max-w-2xl text-base leading-8 sm:text-lg ${mutedText}`}>
                {t.heroText}
              </p>

              <div className="hero-reveal mt-8 flex flex-wrap items-center gap-4">
                <motion.div
                  whileHover={{ y: -4, scale: 1.02 }}
                  className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-6 py-4"
                >
                  <p className={`text-sm ${mutedText}`}>{t.priceLabel}</p>
                  <p className="text-3xl font-bold text-emerald-500">$962</p>
                </motion.div>

                <motion.a
                  href="#contact"
                  whileHover={{ y: -3, scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-6 py-4 text-sm font-semibold text-white shadow-lg shadow-red-900/20"
                >
                  {t.ctaPrimary}
                  <ArrowOutwardIcon style={{ fontSize: 18 }} />
                </motion.a>

                <motion.a
                  href="#services"
                  whileHover={{ y: -3 }}
                  className={`inline-flex items-center gap-2 rounded-2xl border px-6 py-4 text-sm font-semibold ${panelClasses}`}
                >
                  {t.ctaSecondary}
                </motion.a>
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              className="relative flex justify-center lg:justify-end"
            >
              <motion.div
                animate={{ rotateY: [0, 7, 0], rotateX: [0, -4, 0] }}
                transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
                className={`w-full max-w-[560px] rounded-[34px] border p-6 shadow-2xl [perspective:1400px] ${panelClasses}`}
              >
                <div className={`rounded-[26px] border p-6 ${isDark ? 'border-white/10 bg-slate-900' : 'border-slate-200 bg-white'}`}>
                  <div className="flex items-center justify-between border-b border-inherit pb-4">
                    <div>
                      <p className={`text-sm ${subMutedText}`}>Domain Overview</p>
                      <h3 className="text-2xl font-bold">raswapanews.com</h3>
                    </div>
                    <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-xs font-semibold text-emerald-500">
                      {t.available}
                    </span>
                  </div>

                  <div className="mt-5 grid gap-4 sm:grid-cols-2">
                    {[
                      ['Category', 'News / Media / Publishing', PublicIcon],
                      ['Brand Value', 'High recall name', ShieldIcon],
                      ['Project Scope', 'Portal / Media / Campaign', BarChartIcon],
                      ['Support', 'Domain + Full Stack Team', GroupsIcon],
                    ].map(([label, value, Icon]) => (
                      <motion.div
                        key={label}
                        whileHover={{ y: -5 }}
                        className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-white/5' : 'border-slate-200 bg-slate-50'}`}
                      >
                        <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-red-500/10 text-red-500">
                          <Icon style={{ fontSize: 22 }} />
                        </div>
                        <p className={`text-xs uppercase tracking-[0.2em] ${subMutedText}`}>{label}</p>
                        <p className="mt-1 font-semibold">{value}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12 lg:px-10">
        <div className="mb-10 max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-500">{t.whyLabel}</p>
          <h2 className="mt-3 text-3xl font-bold sm:text-4xl">{t.whyTitle}</h2>
        </div>

        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {t.features.map((item, index) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className={`rounded-3xl border p-6 ${panelClasses}`}
              >
                <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-red-500/10 text-red-500">
                  <Icon style={{ fontSize: 28 }} />
                </div>
                <h3 className="text-xl font-semibold">{item.title}</h3>
                <p className={`mt-3 leading-7 ${mutedText}`}>{item.description}</p>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-4 lg:px-10">
        <div className={`rounded-[32px] border p-8 lg:p-10 ${panelClasses}`}>
          <div className="grid gap-8 lg:grid-cols-[1.08fr_0.92fr] lg:items-center">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-500">Team Trust</p>
              <h2 className="mt-3 text-3xl font-bold">{t.trustTitle}</h2>
              <p className={`mt-4 max-w-2xl leading-8 ${mutedText}`}>{t.trustText}</p>

              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {t.trustCards.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.03 }}
                      className={`rounded-2xl border p-5 ${isDark ? 'border-white/10 bg-slate-900/70' : 'border-slate-200 bg-slate-50'}`}
                    >
                      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-red-500/10 text-red-500">
                        <Icon style={{ fontSize: 20 }} />
                      </div>
                      <p className="font-semibold">{item.title}</p>
                    </motion.div>
                  );
                })}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className={`rounded-[28px] border p-6 ${isDark ? 'border-white/10 bg-slate-900/60' : 'border-slate-200 bg-white'}`}
            >
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-red-500/10 text-red-500">
                    <TrendingUpIcon />
                  </div>
                  <div>
                    <p className={`text-sm ${subMutedText}`}>Project Confidence</p>
                    <h3 className="text-xl font-bold">Visible delivery quality</h3>
                  </div>
                </div>

                {[
                  ['UI Quality', 92],
                  ['Frontend Structure', 95],
                  ['Brand Presentation', 89],
                  ['Launch Readiness', 94],
                ].map(([label, value], index) => (
                  <div key={index}>
                    <div className="mb-2 flex justify-between text-sm">
                      <span className={mutedText}>{label}</span>
                      <span className="font-semibold text-red-500">{value}%</span>
                    </div>
                    <div className={`h-3 overflow-hidden rounded-full ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}>
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${value}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: index * 0.12 }}
                        className="h-3 rounded-full bg-gradient-to-r from-red-500 to-emerald-500"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section id="services" className="mx-auto max-w-7xl px-6 py-16 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-2">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`rounded-3xl border p-8 ${panelClasses}`}
          >
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-500">{t.servicesLabel}</p>
            <h2 className="mt-3 text-3xl font-bold">{t.servicesTitle}</h2>
            <p className={`mt-4 leading-8 ${mutedText}`}>{t.servicesText}</p>

            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {t.services.map((service, index) => {
                const Icon = service.icon;
                return (
                  <motion.div
                    key={index}
                    whileHover={{ y: -5 }}
                    className={`rounded-2xl border p-4 ${isDark ? 'border-white/10 bg-slate-900/70' : 'border-slate-200 bg-slate-50'}`}
                  >
                    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-red-500/10 text-red-500">
                      <Icon style={{ fontSize: 22 }} />
                    </div>
                    <p className="font-medium">{service.title}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            id="contact"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className={`rounded-3xl border p-8 ${isDark ? 'border-red-500/20 bg-gradient-to-br from-red-600/10 to-slate-900' : 'border-red-200 bg-gradient-to-br from-red-50 to-white'}`}
          >
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-red-500">{t.contactLabel}</p>
            <h2 className="mt-3 text-3xl font-bold">{t.contactTitle}</h2>
            <p className={`mt-4 leading-8 ${mutedText}`}>{t.contactText}</p>

            <div className="mt-8 grid gap-4 sm:grid-cols-3">
              <div className={`rounded-2xl border p-5 ${panelClasses}`}>
                <p className={`text-sm ${subMutedText}`}>{t.domainPrice}</p>
                <p className="mt-1 text-2xl font-bold text-emerald-500">$962</p>
              </div>
              <div className={`rounded-2xl border p-5 ${panelClasses}`}>
                <p className={`text-sm ${subMutedText}`}>{t.email}</p>
                <p className="mt-1 break-all text-base font-semibold">{t.emailValue}</p>
              </div>
              <div className={`rounded-2xl border p-5 ${panelClasses}`}>
                <p className={`text-sm ${subMutedText}`}>{t.serviceInfo}</p>
                <p className="mt-1 text-base font-semibold">{t.serviceInfoValue}</p>
              </div>
            </div>

            {/* <div className="mt-8 grid gap-6 xl:grid-cols-[1.1fr_0.9fr]"> */}
              <div className={`rounded-3xl border p-6 ${isDark ? 'border-white/10 bg-slate-950/50' : 'border-slate-200 bg-white/80'}`}>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className={`text-sm ${subMutedText}`}>{t.formSub}</p>
                    <h3 className="mt-1 text-xl font-bold">{t.formTitle}</h3>
                  </div>
                  {/* <span className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-500">
                    Frontend
                  </span> */}
                </div>

                <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={`mb-2 block text-sm font-medium ${subMutedText}`}>{t.name}</label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t.name}
                        className={`w-full rounded-2xl border px-4 py-3 outline-none transition ${
                          isDark ? 'border-white/10 bg-slate-900 text-white placeholder:text-slate-500 focus:border-red-400' : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-red-400'
                        }`}
                        required
                      />
                    </div>
                    <div>
                      <label className={`mb-2 block text-sm font-medium ${subMutedText}`}>{t.emailField}</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t.emailField}
                        className={`w-full rounded-2xl border px-4 py-3 outline-none transition ${
                          isDark ? 'border-white/10 bg-slate-900 text-white placeholder:text-slate-500 focus:border-red-400' : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-red-400'
                        }`}
                        required
                      />
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className={`mb-2 block text-sm font-medium ${subMutedText}`}>{t.subject}</label>
                      <input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder={t.subject}
                        className={`w-full rounded-2xl border px-4 py-3 outline-none transition ${
                          isDark ? 'border-white/10 bg-slate-900 text-white placeholder:text-slate-500 focus:border-red-400' : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-red-400'
                        }`}
                        required
                      />
                    </div>
                    <div>
                      <label className={`mb-2 block text-sm font-medium ${subMutedText}`}>{t.phone}</label>
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder={t.phone}
                        className={`w-full rounded-2xl border px-4 py-3 outline-none transition ${
                          isDark ? 'border-white/10 bg-slate-900 text-white placeholder:text-slate-500 focus:border-red-400' : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-red-400'
                        }`}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`mb-2 block text-sm font-medium ${subMutedText}`}>{t.message}</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={5}
                      placeholder={t.message}
                      className={`w-full rounded-2xl border px-4 py-3 outline-none transition ${
                        isDark ? 'border-white/10 bg-slate-900 text-white placeholder:text-slate-500 focus:border-red-400' : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400 focus:border-red-400'
                      }`}
                      required
                    />
                  </div>

                  <div className="flex flex-wrap items-center gap-4 pt-2">
                    <motion.button
                      whileHover={{ y: -2, scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={isSending}
                      className="inline-flex items-center gap-2 rounded-2xl bg-red-600 px-6 py-3 font-semibold text-white transition hover:bg-red-500 disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      <MailOutlineIcon style={{ fontSize: 18 }} />
                      {isSending ? t.sending : t.send}
                    </motion.button>

                    <a
                      href="mailto:raswapanews@gmail.com"
                      className={`inline-flex items-center gap-2 rounded-2xl border px-6 py-3 font-semibold transition hover:translate-y-[-2px] ${panelClasses}`}
                    >
                      <MailOutlineIcon style={{ fontSize: 18 }} />
                      {t.emailBtn}
                    </a>
                  </div>

                  {status.message && (
                    <div
                      className={`rounded-2xl border px-4 py-3 text-sm ${
                        status.type === 'success'
                          ? isDark
                            ? 'border-emerald-400/20 bg-emerald-500/10 text-emerald-300'
                            : 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : isDark
                          ? 'border-red-400/20 bg-red-500/10 text-red-300'
                          : 'border-red-200 bg-red-50 text-red-700'
                      }`}
                    >
                      {status.message}
                    </div>
                  )}
                </form>
              </div>

            
            
          </motion.div>
        </div>
      </section>

      <footer className={`border-t px-6 py-8 text-center text-sm ${isDark ? 'border-white/10 text-slate-400' : 'border-slate-200 text-slate-500'}`}>
        © 2026 raswapanews.com — {t.footer}
      </footer>
    </div>
  );
}
